/**
 * Shared plumbing for the query/mutation hooks.
 *
 * Every backend read/write in the app funnels through {@link unwrap}, which:
 *  - normalises the openapi-fetch `{ data, error, response }` triple,
 *  - throws a typed {@link ApiError} (via {@link toApiError}) on any non-2xx,
 *  - validates the success body against a Zod schema at the trust boundary,
 *  - returns the parsed, typed value.
 *
 * Responses are requested with `parseAs: 'json'` at the call site — even though
 * the generated schema now types response bodies, openapi-fetch still needs the
 * explicit parse hint for these endpoints.
 */

import type { ZodType } from 'zod';
import { ApiError, toApiError } from '../errors';

/**
 * The shape every openapi-fetch `GET/POST/PATCH/PUT` call resolves to.
 *
 * `data` is intentionally `unknown`: the generated response types use
 * `Record<string, never>` placeholders for nullable scalar fields, which do not
 * line up with the canonical Zod entity schemas. The Zod schema is the
 * authoritative source for the parsed shape, so we validate the raw body
 * ourselves rather than trust the generated `data` type here.
 */
export interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/**
 * Validate + unwrap an openapi-fetch result.
 *
 * @param result the `{ data, error, response }` from a typed client call.
 * @param schema a Zod schema for the success body. Its output type is what the
 *   hook returns.
 * @throws {ApiError} on any non-ok response or a body that fails validation.
 */
export function unwrap<Out>(result: FetchResult, schema: ZodType<Out>): Out {
  const { data, error, response } = result;

  if (error !== undefined || !response.ok) {
    throw toApiError(error, response.status);
  }

  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    throw new ApiError(
      'INVALID_RESPONSE',
      'The server returned an unexpected response shape.',
      response.status,
    );
  }
  return parsed.data;
}

/**
 * Unwrap a result whose body is opaque/dynamic (no canonical schema). Validates
 * only the envelope (ok vs error), returns the parsed JSON as a typed value.
 */
export function unwrapRaw<Out>(result: FetchResult): Out {
  const { data, error, response } = result;
  if (error !== undefined || !response.ok) {
    throw toApiError(error, response.status);
  }
  return data as Out;
}
